import type { ButtonHTMLAttributes, ReactNode } from "react";
import styled, { css } from "styled-components";
import { AppButton, type AppButtonVariant } from "@/shared/components";
import verificationLoaderUrl from "@/assets/icons/verification-loader.svg?url";

const Button = styled(AppButton)<{ $verifying: boolean }>`
	width: 100%;
	height: 47px;
	gap: 4px;
	font-family: var(--font-family-app);
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
	border-radius: 64px;
	margin-top: 16px;

	${(props) =>
		props.$verifying &&
		css`
			padding: 8px 12px;
			border: 0;
			background: transparent;
			box-shadow: none;
			color: #303030;
			opacity: 1;
			cursor: wait;

			&:hover,
			&:active,
			&:focus-visible,
			&:disabled {
				border: 0;
				background: transparent;
				box-shadow: none;
				color: #303030;
				opacity: 1;
			}
		`}
`;

const Spinner = styled.img`
	display: block;
	width: 24px;
	height: 24px;
	animation: verification-spin 0.8s linear infinite;

	@keyframes verification-spin {
		to {
			transform: rotate(360deg);
		}
	}
`;

interface VerificationButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	verifying: boolean;
	idleVariant?: Extract<AppButtonVariant, "primary" | "outline">;
	children: ReactNode;
}

export const VerificationButton = ({
	verifying,
	idleVariant = "primary",
	children,
	disabled,
	...buttonProps
}: VerificationButtonProps) => (
	<Button
		{...buttonProps}
		type={buttonProps.type || "button"}
		variant={verifying ? "default" : idleVariant}
		$verifying={verifying}
		disabled={disabled || verifying}
		aria-busy={verifying}
	>
		{verifying ? (
			<>
				<Spinner src={verificationLoaderUrl} alt="" aria-hidden />
				<span>Verifying</span>
			</>
		) : (
			children
		)}
	</Button>
);
